import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/api/client';

export function PortalResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/portal/forgot-password');
    }
  }, [token, navigate]);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/portal/auth/reset-password', { token, password });
      if (!data.data) throw new Error(data.errors?.[0]?.message ?? 'Reset failed');
      return data;
    },
    onSuccess: () => setSuccess(true),
    onError: () => toast.error('This reset link has expired or is invalid'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    mutation.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-10 w-auto" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-text-primary">Vendor Security Portal</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Powered by VeriGRC</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-accent-emerald" />
              <p className="text-base font-medium text-text-primary">Password updated</p>
              <p className="text-sm text-text-secondary">Your password has been reset successfully.</p>
              <Link to="/portal/login" className="mt-2 text-sm text-accent-cyan hover:underline">
                Sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-text-primary">Choose a new password</h2>
                <p className="mt-0.5 text-sm text-text-secondary">Must be at least 12 characters.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="rp-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="rp-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 12 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      autoFocus
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary focus-visible:outline-none"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="rp-confirm">Confirm new password</Label>
                  <Input
                    id="rp-confirm"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={mutation.isPending || !password || !confirm}
                >
                  {mutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating…</>
                  ) : (
                    'Update password'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
